import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { EMPTY, catchError, map, of, switchMap } from 'rxjs';
import {
  DELETE_NON_BPS_PRODUCT_REQUEST,
  GET_NON_BPS_APPROVED_TERMINALS,
  GET_MY_NON_BPS_PRODUCT_REQUESTS,
  GET_NON_BPS_OPERATORS,
  GET_NON_BPS_OPERATOR_BY_UUID,
  GET_NON_BPS_BY_UUID,
  GET_NON_BPS_REQUEST_PROGRESS,
  SAVE_NON_BPS_PRODUCT_ARRIVAL_DETAILS,
  SAVE_NON_BPS_PRODUCT_REQUEST_CONSIGNEE_DETAILS,
  SAVE_NON_BPS_PRODUCT_REQUEST_QUANTITIES,
  SAVE_NON_BPS_PRODUCT_REQUEST_SUPPLIER_DETAILS,
  SAVE_NON_BPS_PRODUCT_REQUEST,
  SUBMIT_NON_BPS_PRODUCT_REQUEST,
} from '../graphql/non-bps-management.graphql';
import {
  DELETE_NON_BPS_ATTACHMENT,
  GET_NON_BPS_ATTACHMENT_TYPES,
  UPLOAD_NON_BPS_ATTACHMENT,
  VIEW_NON_BPS_ATTACHMENT,
} from '@modules/registration/non-bps-registration/graphql/non-bps-registration.graphql';
import {
  NonBpsApprovedTerminalsActions,
  NonBpsDeleteProductRequestActions,
  NonBpsDeleteRequestAttachmentActions,
  NonBpsMyApplicationsActions,
  NonBpsApplicationDetailActions,
  NonBpsRequestAttachmentTypesActions,
  NonBpsRequestProgressActions,
  NonBpsProductRequestDetailActions,
  NonBpsProductRequestsActions,
  NonBpsSaveRequestArrivalDetailsActions,
  NonBpsSaveRequestConsigneeDetailsActions,
  NonBpsSaveRequestQuantitiesActions,
  NonBpsSaveRequestSupplierDetailsActions,
  NonBpsSaveProductRequestActions,
  NonBpsSubmitProductRequestActions,
  NonBpsUploadRequestAttachmentActions,
  NonBpsViewRequestAttachmentActions,
} from './non-bps-management.actions';
import {
  GetNonBpsApprovedTerminalsResponse,
  DeleteNonBpsProductRequestResponse,
  DeleteNonBpsRequestAttachmentResponse,
  GetNonBpsAttachmentTypesResponse,
  GetMyNonBpsOperatorApplicationsResponse,
  GetMyNonBpsProductRequestsResponse,
  GetNonBpsOperatorByUuidResponse,
  NonBpsProductRequest,
  GetNonBpsProductRequestByUuidResponse,
  GetNonBpsProductRequestProgressResponse,
  SaveNonBpsProductRequestResponse,
  SaveNonBpsProductRequestArrivalDetailsResponse,
  SaveNonBpsProductRequestConsigneeDetailsResponse,
  SaveNonBpsProductRequestQuantitiesResponse,
  SaveNonBpsProductRequestSupplierDetailsResponse,
  SubmitNonBpsProductRequestResponse,
  UploadNonBpsRequestAttachmentResponse,
  ViewNonBpsRequestAttachmentResponse,
} from '../responses/non-bps-management.response';
import { GqlResponse } from '@shared/graphql-response/graphql.response';

@Injectable({ providedIn: 'root' })
export class NonBpsManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphQlService = inject(GraphQlService);

  private normalizeProductRequest(payload: NonBpsProductRequest | null | undefined): NonBpsProductRequest | null {
    if (!payload) {
      return null;
    }

    const quantities = payload.productQuantities ?? payload.quantities ?? [];
    const mappedConsignees = quantities.map((quantity) => ({
      quantityUuid: quantity.uuid ?? null,
      consignees: quantity.consignees ?? [],
    }));
    const hasTopLevelConsignees = (payload.consignees?.length ?? 0) > 0;
    const hasProductConsignees = (payload.productConsignees?.length ?? 0) > 0;

    return {
      ...payload,
      terminalUuid: payload.terminalUuid ?? payload.terminal?.uuid ?? null,
      quantities,
      consignees: hasTopLevelConsignees ? payload.consignees : (hasProductConsignees ? payload.productConsignees : mappedConsignees),
      productConsignees: hasProductConsignees ? payload.productConsignees : (hasTopLevelConsignees ? payload.consignees : mappedConsignees),
    };
  }

  private isSuccessStatus(status?: boolean | string): boolean {
    return status === true || status === 'SUCCESS';
  }

  private buildFailure(message?: string) {
    return { success: false, statusCode: 400, message: message ?? 'Network error', data: null };
  }

  private buildSuccess<T>(payload: GqlResponse<T>, fallbackMessage: string) {
    return {
      success: true,
      statusCode: 200,
      message: payload.message ?? fallbackMessage,
      data: payload.data,
    };
  }

  readonly loadMyApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsMyApplicationsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetMyNonBpsOperatorApplicationsResponse>({
            query: GET_NON_BPS_OPERATORS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageable: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getMyNonBpsOperatorApplications;
              if (!payload) {
                return NonBpsMyApplicationsActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }
              return NonBpsMyApplicationsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Applications loaded',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsMyApplicationsActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadApplicationDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsApplicationDetailActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsOperatorByUuidResponse>({
            query: GET_NON_BPS_OPERATOR_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getNonBpsOperatorByUuid;
              if (!payload) {
                return NonBpsApplicationDetailActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }
              return NonBpsApplicationDetailActions.success({
                response: {
                  success: payload.status === true,
                  statusCode: 200,
                  message: payload.message ?? 'Application loaded',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsApplicationDetailActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadProductRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsProductRequestsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetMyNonBpsProductRequestsResponse>({
            query: GET_MY_NON_BPS_PRODUCT_REQUESTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageable: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getMyNonBpsProductRequests;
              if (!payload) {
                return NonBpsProductRequestsActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }
              return NonBpsProductRequestsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Discharge Window Requests loaded',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsProductRequestsActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadProductRequestDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsProductRequestDetailActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsProductRequestByUuidResponse>({
            query: GET_NON_BPS_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getNonBpsProductRequestByUuid;
              if (!payload) {
                return NonBpsProductRequestDetailActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }

              const normalizedPayload = this.normalizeProductRequest(payload.data);

              return NonBpsProductRequestDetailActions.success({
                response: {
                  success: payload.status === true,
                  statusCode: 200,
                  message: payload.message ?? 'Discharge Window Request loaded',
                  data: normalizedPayload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsProductRequestDetailActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadApprovedTerminals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsApprovedTerminalsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsApprovedTerminalsResponse>({
            query: GET_NON_BPS_APPROVED_TERMINALS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getAllTerminalRegistration;
              if (!payload) {
                return NonBpsApprovedTerminalsActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }

              return NonBpsApprovedTerminalsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Approved terminals loaded',
                  data: {
                    ...payload,
                    content: (payload.content ?? []).filter(
                      (terminal) => terminal?.status === 'APPROVED',
                    ),
                  },
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsApprovedTerminalsActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to load approved terminals'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadRequestProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsRequestProgressActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsProductRequestProgressResponse>({
            query: GET_NON_BPS_REQUEST_PROGRESS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getNonBpsProductRequestProgress;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsRequestProgressActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to load request progress'),
                });
              }

              return NonBpsRequestProgressActions.success({
                response: this.buildSuccess(payload, 'Request progress loaded'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsRequestProgressActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to load request progress'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadRequestAttachmentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsRequestAttachmentTypesActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsAttachmentTypesResponse>({
            query: GET_NON_BPS_ATTACHMENT_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { registrationType: request },
          })
          .pipe(
            map((result) =>
              NonBpsRequestAttachmentTypesActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Attachment types loaded',
                  data: result.data?.getAttachmentTypesByRegistrationType ?? [],
                },
              }),
            ),
            catchError((error) =>
              of(
                NonBpsRequestAttachmentTypesActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to load attachment types'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveProductRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSaveProductRequestActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsProductRequestResponse>({
            mutation: SAVE_NON_BPS_PRODUCT_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsProductRequest;
              if (!payload) {
                return NonBpsSaveProductRequestActions.failure({
                  error: this.buildFailure('No response returned'),
                });
              }

              const isSuccess = payload.status === true;
              if (!isSuccess) {
                return NonBpsSaveProductRequestActions.failure({
                  error: this.buildFailure(payload.message),
                });
              }

              return NonBpsSaveProductRequestActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Discharge Window Request saved',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsSaveProductRequestActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveRequestQuantities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSaveRequestQuantitiesActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsProductRequestQuantitiesResponse>({
            mutation: SAVE_NON_BPS_PRODUCT_REQUEST_QUANTITIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsProductRequestQuantities;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsSaveRequestQuantitiesActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to save quantities'),
                });
              }

              return NonBpsSaveRequestQuantitiesActions.success({
                response: this.buildSuccess(payload, 'Quantities saved'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsSaveRequestQuantitiesActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to save quantities'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveRequestArrivalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSaveRequestArrivalDetailsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsProductRequestArrivalDetailsResponse>({
            mutation: SAVE_NON_BPS_PRODUCT_ARRIVAL_DETAILS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsProductRequestArrivalDetails;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsSaveRequestArrivalDetailsActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to save arrival details'),
                });
              }

              return NonBpsSaveRequestArrivalDetailsActions.success({
                response: this.buildSuccess(payload, 'Arrival details saved'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsSaveRequestArrivalDetailsActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to save arrival details'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveRequestSupplierDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSaveRequestSupplierDetailsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsProductRequestSupplierDetailsResponse>({
            mutation: SAVE_NON_BPS_PRODUCT_REQUEST_SUPPLIER_DETAILS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsProductRequestSupplierDetails;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsSaveRequestSupplierDetailsActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to save supplier details'),
                });
              }

              return NonBpsSaveRequestSupplierDetailsActions.success({
                response: this.buildSuccess(payload, 'Supplier details saved'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsSaveRequestSupplierDetailsActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to save supplier details'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveRequestConsigneeDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSaveRequestConsigneeDetailsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsProductRequestConsigneeDetailsResponse>({
            mutation: SAVE_NON_BPS_PRODUCT_REQUEST_CONSIGNEE_DETAILS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsProductRequestConsigneeDetails;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsSaveRequestConsigneeDetailsActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to save consignee details'),
                });
              }

              return NonBpsSaveRequestConsigneeDetailsActions.success({
                response: this.buildSuccess(payload, 'Consignee details saved'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsSaveRequestConsigneeDetailsActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to save consignee details'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly uploadRequestAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsUploadRequestAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<UploadNonBpsRequestAttachmentResponse>({
            mutation: UPLOAD_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: {
                attachableUuid: request.attachableUuid,
                registrationAttachmentTypeUuid: request.registrationAttachmentTypeUuid,
                fileDto: {
                  filename: request.filename,
                  extension: request.extension,
                  data: request.data,
                },
              },
            },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.uploadAttachment;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsUploadRequestAttachmentActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Attachment upload failed'),
                });
              }

              return NonBpsUploadRequestAttachmentActions.success({
                response: this.buildSuccess(payload, 'Attachment uploaded'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsUploadRequestAttachmentActions.failure({
                  error: this.buildFailure(error?.message ?? 'Attachment upload failed'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly viewRequestAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsViewRequestAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ViewNonBpsRequestAttachmentResponse>({
            query: VIEW_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.viewAttachmentByUuid;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsViewRequestAttachmentActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to load attachment'),
                });
              }

              return NonBpsViewRequestAttachmentActions.success({
                response: this.buildSuccess(payload, 'Attachment loaded'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsViewRequestAttachmentActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to load attachment'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly deleteRequestAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsDeleteRequestAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteNonBpsRequestAttachmentResponse>({
            mutation: DELETE_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.deleteAttachment;
              if (!payload || !this.isSuccessStatus(payload.status)) {
                return NonBpsDeleteRequestAttachmentActions.failure({
                  error: this.buildFailure(payload?.message ?? 'Failed to delete attachment'),
                });
              }

              return NonBpsDeleteRequestAttachmentActions.success({
                response: this.buildSuccess(payload, 'Attachment deleted'),
              });
            }),
            catchError((error) =>
              of(
                NonBpsDeleteRequestAttachmentActions.failure({
                  error: this.buildFailure(error?.message ?? 'Failed to delete attachment'),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly submitProductRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSubmitProductRequestActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitNonBpsProductRequestResponse>({
            mutation: SUBMIT_NON_BPS_PRODUCT_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.submitNonBpsProductRequestToWorkflow;
              if (!payload) {
                return NonBpsSubmitProductRequestActions.failure({
                  error: this.buildFailure('No response returned'),
                });
              }

              const isSuccess = payload.status === true;
              if (!isSuccess) {
                return NonBpsSubmitProductRequestActions.failure({
                  error: this.buildFailure(payload.message),
                });
              }

              return NonBpsSubmitProductRequestActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Discharge Window Request submitted',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsSubmitProductRequestActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly deleteProductRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsDeleteProductRequestActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteNonBpsProductRequestResponse>({
            mutation: DELETE_NON_BPS_PRODUCT_REQUEST,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.deleteNonBpsProductRequest;
              if (!payload) {
                return NonBpsDeleteProductRequestActions.failure({
                  error: this.buildFailure('No response returned'),
                });
              }

              const isSuccess = payload.status === true;
              if (!isSuccess) {
                return NonBpsDeleteProductRequestActions.failure({
                  error: this.buildFailure(payload.message),
                });
              }

              return NonBpsDeleteProductRequestActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Discharge Window Request deleted',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsDeleteProductRequestActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly refreshRequestProgressAfterStepSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NonBpsSaveRequestQuantitiesActions.success,
        NonBpsSaveRequestArrivalDetailsActions.success,
        NonBpsSaveRequestSupplierDetailsActions.success,
        NonBpsSaveRequestConsigneeDetailsActions.success,
        NonBpsSubmitProductRequestActions.success,
      ),
      switchMap(({ response }) => {
        const uuid = response?.data?.uuid;
        if (!uuid) {
          return EMPTY;
        }

        return of(
          NonBpsRequestProgressActions.request({ request: uuid }),
          NonBpsProductRequestDetailActions.request({ request: uuid }),
        );
      }),
    ),
  );
}
